// poll: the shell's safety-net reads, kept quiet.
//
// The dashboard re-reads a few things on a cadence (the open folder, the project
// list) because live updates can fail without a sound. A fixed setInterval is a
// bad way to do it: it keeps asking while the tab is hidden, keeps asking while
// the network is gone, and keeps asking at full pace of a server that is already
// struggling. So a poll here:
//
//   · is silent while the tab is hidden or the browser is offline, and wakes the
//     moment the tab returns or the network does (a read that is due runs then);
//   · after each failure waits twice as long, up to a minute, and goes back to
//     its normal pace on the first success;
//   · schedules the NEXT run only when the last one is over, so a slow answer
//     never stacks a second request behind it.
//
// `run` says whether it worked (true) or not (false). It decides what a failure
// is; a thrown error counts as one.

/** the longest a failing poll waits between tries. */
export const POLL_BACKOFF_CAP_MS = 60_000;

/** the wait after `failures` failures in a row: the normal pace, doubled each
 *  time, never past the cap (and never below the normal pace). */
export function backoffMs(every: number, failures: number, cap = POLL_BACKOFF_CAP_MS): number {
  if (!(failures > 0)) return every;
  const doubled = every * 2 ** Math.min(failures, 10);
  return Math.max(every, Math.min(cap, doubled));
}

/** what a poll needs to know about the page. The browser's own by default; a
 *  test hands in a fake one. */
export interface PollEnv {
  hidden(): boolean;
  online(): boolean;
  /** call `fn` when the tab becomes visible or the network returns. */
  onWake(fn: () => void): () => void;
  now(): number;
}

export const browserPollEnv: PollEnv = {
  hidden: () => typeof document !== "undefined" && document.hidden,
  online: () => typeof navigator === "undefined" || navigator.onLine !== false,
  onWake(fn) {
    if (typeof document === "undefined" || typeof window === "undefined") return () => {};
    const onVis = () => {
      if (!document.hidden) fn();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", fn);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", fn);
    };
  },
  now: () => Date.now(),
};

export interface PollOptions {
  /** the normal pace, in ms. The first read comes one pace after the start. */
  every: number;
  run: () => Promise<boolean>;
  cap?: number;
  env?: PollEnv;
}

/** Start a poll. Returns the function that stops it. */
export function startPoll({ every, run, cap = POLL_BACKOFF_CAP_MS, env = browserPollEnv }: PollOptions): () => void {
  let stopped = false;
  let running = false;
  let failures = 0;
  let lastAt = env.now();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const arm = (ms: number) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(tick, ms);
  };

  async function tick() {
    timer = null;
    if (stopped || running) return;
    // paused: no request. Look again at the normal pace (the wake below is the
    // fast path; this covers a browser that reports "offline" and never says
    // otherwise).
    if (env.hidden() || !env.online()) {
      arm(every);
      return;
    }
    running = true;
    let ok = false;
    try {
      ok = await run();
    } catch {
      ok = false;
    }
    running = false;
    if (stopped) return;
    lastAt = env.now();
    failures = ok ? 0 : failures + 1;
    arm(backoffMs(every, failures, cap));
  }

  // the tab is back, or the network is: whatever was waiting runs now if it was
  // due, and a poll that had backed off starts over at its normal pace.
  const unwake = env.onWake(() => {
    if (stopped || running || env.hidden() || !env.online()) return;
    failures = 0;
    arm(Math.max(0, every - (env.now() - lastAt)));
  });

  arm(every);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    timer = null;
    unwake();
  };
}
